/**
 * Rough blur / readability score via Laplacian variance on a downscaled canvas.
 * Higher = sharper. Typical thresholds: < 40 very blurry, > 80 OK for documents.
 */
export async function estimateImageSharpness(blob: Blob): Promise<number> {
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('read failed'));
      el.src = url;
    });

    const maxSide = 320;
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(32, Math.round(img.naturalWidth * scale));
    const h = Math.max(32, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return 100;
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const gray = new Float32Array(w * h);
    for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
      gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }

    let sum = 0;
    let sumSq = 0;
    let n = 0;
    for (let y = 1; y < h - 1; y += 1) {
      for (let x = 1; x < w - 1; x += 1) {
        const i = y * w + x;
        const lap =
          -4 * gray[i] +
          gray[i - 1] +
          gray[i + 1] +
          gray[i - w] +
          gray[i + w];
        sum += lap;
        sumSq += lap * lap;
        n += 1;
      }
    }
    if (!n) return 100;
    const mean = sum / n;
    return sumSq / n - mean * mean;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const BLUR_WARN_THRESHOLD = 45;

export function blurWarningMessage(score: number): string | null {
  if (score >= BLUR_WARN_THRESHOLD) return null;
  return 'Снимок выглядит размытым. Переснимите при хорошем освещении, чтобы данные паспорта читались.';
}
